'use client';

import { useEffect, useRef } from 'react';

type MiniCartGuestCartParams = {
  isGuest: boolean;
  /** The guest cart this browser holds. */
  guestCartId?: string;
  /** The provider's own cart id. */
  cartId?: string;
  setCartId: (cartId?: string) => void;
  setEnableCartFetching: (enabled: boolean) => void;
};

/**
 * commerce-core's provider seeds its cart id and fetching switch once. A
 * guest's cart appears, is replaced by another tab's, or is forgotten while
 * the provider is mounted, so both follow it from here instead of a remount.
 * Fetching stays off without a cart: commerce-core would create one.
 *
 * Call it below CartManagementProvider, with the provider's own values.
 */
export const useMiniCartGuestCart = ({
  isGuest,
  guestCartId,
  cartId,
  setCartId,
  setEnableCartFetching,
}: MiniCartGuestCartParams): void => {
  const cartIdRef = useRef(cartId);
  cartIdRef.current = cartId;
  const hasGuestCart = Boolean(guestCartId);

  // On a change of the stored id alone: the provider was seeded from the
  // stored cart, while the first render of a hydrated page still holds the
  // server's snapshot of it (no cart). A replaced cart moves the provider
  // first (useFollowGuestCart), and the store catches up a render later.
  const previousGuestCartIdRef = useRef(guestCartId);
  useEffect(() => {
    if (previousGuestCartIdRef.current === guestCartId) return;
    previousGuestCartIdRef.current = guestCartId;
    if (!isGuest || guestCartId === cartIdRef.current) return;
    setCartId(guestCartId);
  }, [isGuest, guestCartId, setCartId]);

  // Only on a change: the provider was seeded to match, and a PDP sharing it
  // turns fetching on itself to start a guest's first cart.
  const hadGuestCartRef = useRef(hasGuestCart);
  useEffect(() => {
    if (hadGuestCartRef.current === hasGuestCart) return;
    hadGuestCartRef.current = hasGuestCart;
    if (isGuest) setEnableCartFetching(hasGuestCart);
  }, [isGuest, hasGuestCart, setEnableCartFetching]);
};
